import { createContext, useContext, useEffect, useState } from 'react';
import {
  List, FunctionField, EditButton, Edit, Create, SimpleForm,
  TextInput, NumberInput, ImageInput, ImageField, CreateButton, SearchInput, SelectInput,
  useRecordContext, useDataProvider, useNotify, useRefresh, useRedirect, useGetList, Button,
} from 'react-admin';
import {
  Box, Chip, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  Button as MuiButton, ToggleButton, ToggleButtonGroup, CircularProgress,
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,
  Stepper, Step, StepLabel, Backdrop, Divider, IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { PageHeader } from '../components/PageHeader';
import { ResponsiveDataList } from '../components/ResponsiveDataList';
import { StatRow } from '../components/StatCards';
import { dataTableScrollSx, formPageSx, pageShellSx, resourceListSx } from '../ui';
import { extractDebtSlip } from '../services/claudeVision';
import { getSettings } from '../services/settings';
import { findMatches } from '../services/matching';
import { compressImage, splitDataUrl } from '../services/imageUtils';
import { formatVnd } from '../utils/format';
import type { AutomationStatus, DebtSlip, VatInvoice, Supplier } from '../types';
import { runDebtAutomation } from '../services/debtAutomation';
import { createPaymentOrderForMatch, findSupplierForInvoice } from '../services/paymentOrderAutomation';

// ---------- busy context: khóa toàn form khi AI đang chạy ----------
const BusyCtx = createContext<{ busy: boolean; setBusy: (b: boolean) => void }>({
  busy: false,
  setBusy: () => {},
});
const useBusy = () => useContext(BusyCtx);

type ActiveAutomationStage = Exclude<AutomationStatus, 'completed' | 'needs_review' | 'failed'>;

const ACTIVE_AUTOMATION_STAGES: ActiveAutomationStage[] = [
  'queued',
  'extracting',
  'syncing_meinvoice_suppliers',
  'syncing_meinvoice_invoices',
  'matching',
  'enriching_bank',
  'creating_order',
];

const AUTOMATION_STAGE_META = {
  queued: {
    shortLabel: 'Khởi tạo',
    title: 'Đang khởi động quy trình',
    detail: 'Chuẩn bị dữ liệu và sắp xếp các tác vụ tự động.',
    progress: 8,
    color: '#F6C85F',
    icon: <PendingActionsRoundedIcon />,
  },
  extracting: {
    shortLabel: 'AI bóc tách',
    title: 'AI đang đọc chứng từ',
    detail: 'Nhận diện nhà cung cấp, số tiền, ngày chứng từ và thông tin thanh toán.',
    progress: 24,
    color: '#73D2FF',
    icon: <AutoAwesomeRoundedIcon />,
  },
  syncing_meinvoice_suppliers: {
    shortLabel: 'NCC meInvoice',
    title: 'Đang đồng bộ nhà cung cấp',
    detail: 'Lấy hồ sơ nhà cung cấp mới nhất từ meInvoice.',
    progress: 42,
    color: '#A78BFA',
    icon: <CloudSyncRoundedIcon />,
  },
  syncing_meinvoice_invoices: {
    shortLabel: 'Hóa đơn meInvoice',
    title: 'Đang tải hóa đơn VAT',
    detail: 'Đồng bộ toàn bộ danh sách hóa đơn đầu vào từ meInvoice.',
    progress: 60,
    color: '#FF9F7A',
    icon: <ReceiptLongIcon />,
  },
  matching: {
    shortLabel: 'Đối soát',
    title: 'Đang tìm hóa đơn khớp nhất',
    detail: 'So sánh nhà cung cấp, số tiền và ngày chứng từ để chọn kết quả an toàn.',
    progress: 78,
    color: '#55E6C1',
    icon: <CompareArrowsIcon />,
  },
  enriching_bank: {
    shortLabel: 'Ngân hàng',
    title: 'Đang lấy thông tin ngân hàng',
    detail: 'Đọc tài khoản người bán từ chi tiết hóa đơn meInvoice đã khớp.',
    progress: 88,
    color: '#38BDF8',
    icon: <AccountBalanceRoundedIcon />,
  },
  creating_order: {
    shortLabel: 'Tạo lệnh',
    title: 'Đang hoàn thiện lệnh chi',
    detail: 'Kiểm tra tài khoản ngân hàng và tạo lệnh thanh toán chờ duyệt.',
    progress: 94,
    color: '#7CFF8A',
    icon: <TaskAltRoundedIcon />,
  },
} as const;

// ---------- shared field renderers ----------
const STATUS: Record<string, { label: string; color: any }> = {
  new: { label: 'Mới tải lên', color: 'default' },
  extracted: { label: 'Đã bóc tách', color: 'info' },
  matched: { label: 'Đã đối soát', color: 'primary' },
  ordered: { label: 'Đã tạo lệnh', color: 'success' },
};

const STEP_ORDER: DebtSlip['status'][] = ['new', 'extracted', 'matched', 'ordered'];
const STEPS = ['Bóc tách', 'Đối soát', 'Tạo lệnh', 'Duyệt'];

const StatusChip = () => {
  const r = useRecordContext<DebtSlip>();
  if (!r) return null;
  const s = STATUS[r.status] || STATUS.new;
  return <Chip size="small" label={s.label} color={s.color} />;
};

const Thumb = () => {
  const r = useRecordContext<DebtSlip>();
  if (!r?.imageDataUrl) return <Typography color="text.disabled">—</Typography>;
  return (
    <Box
      component="img"
      src={r.imageDataUrl}
      alt="Ảnh đơn nợ"
      sx={{
        width: 62,
        height: 62,
        objectFit: 'cover',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#F3F6F7',
        boxShadow: '0 8px 18px -14px rgba(44,30,101,.7)',
      }}
    />
  );
};

// ---------- LIST ----------
const ListEmpty = () => (
  <Box sx={{ textAlign: 'center', p: { xs: 4, md: 8 } }}>
    <Box sx={{ width: 72, height: 72, mx: 'auto', mb: 1.5, display: 'grid', placeItems: 'center', borderRadius: '12px', bgcolor: 'rgba(14,116,144,.08)', color: 'primary.main' }}>
      <ReceiptLongIcon sx={{ fontSize: 38 }} />
    </Box>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 850 }}>Chưa có đơn nợ nào</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Tải ảnh phiếu để AI bóc tách, đối soát và chuẩn bị lệnh thanh toán.
    </Typography>
    <CreateButton label="Tải ảnh đơn nợ" variant="contained" />
  </Box>
);

const DebtSlipStats = () => {
  const { data = [] } = useGetList<DebtSlip>('debtSlips', {
    pagination: { page: 1, perPage: 1000 }, sort: { field: 'uploadedAt', order: 'DESC' },
  });
  const by = (status: string) => data.filter((x) => x.status === status).length;
  return (
    <StatRow
      stats={[
        { label: 'Tổng đơn nợ', value: data.length, sub: 'Phiếu đã tải lên', icon: <InboxRoundedIcon />, color: '#0E7490' },
        { label: 'Đã bóc tách', value: by('extracted'), sub: `${data.length ? Math.round((by('extracted') / data.length) * 100) : 0}% tổng số đơn`, icon: <AutoAwesomeRoundedIcon />, color: '#2F80ED' },
        { label: 'Đã tạo lệnh', value: by('ordered'), sub: `${data.length ? Math.round((by('ordered') / data.length) * 100) : 0}% tổng số đơn`, icon: <TaskAltRoundedIcon />, color: '#0FAF78' },
        { label: 'Chờ xử lý', value: by('new'), sub: `${data.length ? Math.round((by('new') / data.length) * 100) : 0}% tổng số đơn`, icon: <PendingActionsRoundedIcon />, color: '#F59E0B' },
      ]}
    />
  );
};

const debtSlipFilters = [
  <SearchInput key="q" source="q" placeholder="Tìm kiếm theo nhà cung cấp..." alwaysOn />,
  <SelectInput
    key="status"
    source="status"
    label="Trạng thái"
    choices={Object.entries(STATUS).map(([id, item]) => ({ id, name: item.label }))}
    alwaysOn
  />,
];

export const DebtSlipList = () => (
  <Box sx={pageShellSx}>
    <PageHeader
      title="Đơn nợ"
      subtitle="Quản lý các phiếu đã tải lên và theo dõi toàn bộ tiến trình xử lý bằng AI."
      icon={<ReceiptLongIcon />}
      actions={<CreateButton label="Tải ảnh đơn nợ" variant="contained" />}
    />
    <DebtSlipStats />
    <List
      sx={resourceListSx}
      actions={false}
      filters={debtSlipFilters}
      perPage={25}
      sort={{ field: 'uploadedAt', order: 'DESC' }}
      title="Đơn nợ"
      empty={<ListEmpty />}
    >
      <ResponsiveDataList<DebtSlip>
        ariaLabel="Danh sách đơn nợ"
        primaryText={(r) => r.supplierName || '(chưa bóc tách)'}
        secondaryText={(r) => `Tổng phải trả: ${formatVnd(r.totalPayable)}`}
        tertiaryText={(r) => STATUS[r.status]?.label || r.status}
        leftAvatar={(r) => r.imageDataUrl || '?'}
      >
        <FunctionField label="Ảnh" render={() => <Thumb />} />
        <FunctionField
          label="Nhà cung cấp"
          render={(r: DebtSlip) => <Typography variant="body2" sx={{ fontWeight: 750, minWidth: 250 }}>{r.supplierName || '(chưa bóc tách)'}</Typography>}
        />
        <FunctionField
          label="Tổng phải trả"
          textAlign="right"
          render={(r: DebtSlip) => <Typography variant="body2" sx={{ fontWeight: 850, textAlign: 'right' }}>{formatVnd(r.totalPayable)}</Typography>}
        />
        <FunctionField label="Trạng thái" render={() => <StatusChip />} />
        <EditButton label="Sửa" />
      </ResponsiveDataList>
    </List>
  </Box>
);

// ---------- CREATE (upload) ----------
// Nén + resize ảnh (~1600px) trước khi lưu để IndexedDB nhẹ mà AI vẫn đọc tốt.
const transformCreate = async (data: any) => {
  let imageDataUrl: string | undefined;
  let imageMime: string | undefined;
  if (data.image?.rawFile) {
    imageDataUrl = await compressImage(data.image.rawFile, { maxDim: 1600, quality: 0.82 });
    imageMime = splitDataUrl(imageDataUrl).mediaType;
  }
  return {
    imageDataUrl,
    imageMime,
    status: 'new',
    matchStatus: 'none',
    automationStatus: 'queued',
    automationStep: 'queued',
    automationAttempts: 0,
    uploadedAt: new Date().toISOString(),
  };
};

export const DebtSlipCreate = () => {
  const dp = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const onSuccess = (data: DebtSlip) => {
    redirect('edit', 'debtSlips', data.id);
    notify('Đã lưu ảnh. Quy trình tự động đang bắt đầu…', { type: 'info' });
    void runDebtAutomation(dp, data.id)
      .then((order) => {
        notify('Quy trình tự động đã hoàn tất, lệnh chi đang chờ duyệt.', { type: 'success' });
        if (order?.id != null) redirect('edit', 'paymentOrders', order.id);
      })
      .catch((error: any) => notify(String(error?.message || 'Quy trình cần được kiểm tra thủ công.'), { type: 'warning' }))
      .finally(refresh);
  };
  return (
    <Box sx={pageShellSx}>
      <PageHeader
        title="Tải ảnh đơn nợ"
        subtitle="Chọn ảnh từ điện thoại hoặc máy tính. Hệ thống sẽ tự nén và xử lý sau khi lưu."
        icon={<ImageRoundedIcon />}
      />
      <Create title="Tải ảnh đơn nợ" transform={transformCreate} mutationOptions={{ onSuccess }} sx={formPageSx}>
        <SimpleForm>
          <Box sx={{ width: '100%' }}>
            <Alert severity="info" sx={{ mb: 2.2 }}>
              Nên dùng ảnh rõ nét, đủ sáng và không mất góc. Sau khi lưu, hệ thống tự bóc tách, đồng bộ meInvoice, đối soát và tạo lệnh chờ duyệt.
            </Alert>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: '12px',
                border: '1px dashed rgba(14,116,144,.30)',
                bgcolor: 'rgba(14,116,144,.025)',
                '& .RaFileInput-dropZone': { border: 0, bgcolor: 'transparent', minHeight: 220 },
              }}
            >
              <ImageInput source="image" label="Ảnh đơn nợ" accept={{ 'image/*': [] }}>
                <ImageField source="src" title="title" />
              </ImageInput>
            </Box>
          </Box>
        </SimpleForm>
      </Create>
    </Box>
  );
};

// ---------- action buttons (operate on persisted record) ----------
const ExtractButton = () => {
  const record = useRecordContext<DebtSlip>();
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { busy, setBusy } = useBusy();
  const [loading, setLoading] = useState(false);
  if (!record) return null;

  const onClick = async () => {
    if (!record.imageDataUrl) return;
    setLoading(true);
    setBusy(true);
    try {
      const s = await getSettings();
      const r = await extractDebtSlip(record.imageDataUrl);
      await dp.update('debtSlips', {
        id: record.id,
        data: {
          ...record,
          supplierName: r.supplierName ?? record.supplierName,
          taxCode: r.taxCode ?? record.taxCode,
          slipNumber: r.slipNumber ?? record.slipNumber,
          slipDate: r.slipDate ?? record.slipDate,
          currentAmount: r.currentAmount ?? record.currentAmount,
          oldDebt: r.oldDebt ?? record.oldDebt,
          totalPayable: r.totalPayable ?? record.totalPayable,
          bankInfoRaw: r.bankInfoRaw ?? record.bankInfoRaw,
          rawText: r.rawText ?? record.rawText,
          aiConfidence: r.confidence,
          aiModel: s.model,
          extractedAt: new Date().toISOString(),
          status: record.status === 'new' ? 'extracted' : record.status,
        },
        previousData: record,
      });
      notify('AI đã bóc tách xong', { type: 'success' });
      refresh();
    } catch {
      notify('Không thể bóc tách dữ liệu từ ảnh. Vui lòng kiểm tra cấu hình AI và thử lại.', { type: 'error' });
    } finally {
      setLoading(false);
      setBusy(false);
    }
  };

  return (
    <Button
      label={loading ? 'Đang đọc phiếu…' : 'AI bóc tách'}
      variant="contained"
      onClick={onClick}
      disabled={busy || !record.imageDataUrl}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
    />
  );
};

const MatchButton = () => {
  const record = useRecordContext<DebtSlip>();
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { busy } = useBusy();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [basis, setBasis] = useState<'total' | 'current'>('total');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<VatInvoice[]>([]);

  if (!record) return null;

  const compute = async (b: 'total' | 'current', invs?: VatInvoice[]) => {
    const s = await getSettings();
    const list = invs || invoices;
    const amount = b === 'total' ? record.totalPayable : record.currentAmount;
    const c = findMatches(record, list, {
      tolerancePct: s.matchAmountTolerancePct,
      toleranceAbs: s.matchAmountToleranceAbs,
      dateWindowDays: s.matchDateWindowDays,
      matchAmount: amount || 0,
    });
    setCandidates(c);
  };

  const onOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await dp.getList<VatInvoice>('vatInvoices', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'invoiceDate', order: 'DESC' },
        filter: {},
      });
      setInvoices(data);
      await compute(basis, data);
    } catch {
      notify('Không thể đối soát dữ liệu. Vui lòng thử lại.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onChangeBasis = async (_: any, v: 'total' | 'current' | null) => {
    if (!v) return;
    setBasis(v);
    await compute(v);
  };

  const choose = async (inv: VatInvoice, score: number) => {
    await dp.update('debtSlips', {
      id: record.id,
      data: { ...record, matchedInvoiceId: inv.id, matchStatus: 'confirmed', matchScore: score, status: 'matched' },
      previousData: record,
    });
    notify(`Đã khớp với HĐ ${inv.invoiceNumber}`, { type: 'success' });
    setOpen(false);
    refresh();
  };

  return (
    <>
      <Button label="Đối soát" variant="contained" onClick={onOpen} disabled={busy || record.status === 'new'} startIcon={<CompareArrowsIcon />} />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Đối soát với hóa đơn VAT</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Khớp theo số tiền:&nbsp;
              <b>{formatVnd(basis === 'total' ? record.totalPayable : record.currentAmount)}</b>
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={basis} onChange={onChangeBasis}>
              <ToggleButton value="total">Tổng phải trả ({formatVnd(record.totalPayable)})</ToggleButton>
              <ToggleButton value="current">Đơn lần này ({formatVnd(record.currentAmount)})</ToggleButton>
            </ToggleButtonGroup>
            {record.oldDebt ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Phiếu này có nợ cũ {formatVnd(record.oldDebt)}. Nếu khớp theo "Tổng phải trả" không ra HĐ,
                hãy thử "Đơn lần này".
              </Alert>
            ) : null}
          </Box>
          {loading ? (
            <CircularProgress />
          ) : candidates.length === 0 ? (
            <Alert severity="info">Không tìm thấy HĐ phù hợp. Thử đổi cơ sở khớp hoặc kiểm tra HĐ VAT.</Alert>
          ) : (
            <TableContainer
              component={Box}
              tabIndex={0}
              role="region"
              aria-label="Bảng kết quả đối soát hóa đơn"
              sx={dataTableScrollSx}
            >
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Số HĐ</TableCell>
                    <TableCell>Nhà cung cấp</TableCell>
                    <TableCell align="right">Số tiền</TableCell>
                    <TableCell>Điểm</TableCell>
                    <TableCell>Lý do</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.map((c) => (
                    <TableRow key={String(c.invoice.id)}>
                      <TableCell>{c.invoice.invoiceNumber}</TableCell>
                      <TableCell>{c.invoice.supplierName}</TableCell>
                      <TableCell align="right">{formatVnd(c.invoice.amount)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={`${Math.round(c.score * 100)}%`} color={c.score > 0.6 ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{c.reasons.join(', ')}</Typography>
                      </TableCell>
                      <TableCell>
                        <MuiButton size="small" variant="contained" onClick={() => choose(c.invoice, c.score)}>
                          Chọn
                        </MuiButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setOpen(false)}>Đóng</MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CreateOrderButton = () => {
  const record = useRecordContext<DebtSlip>();
  const dp = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const { busy, setBusy } = useBusy();
  const [loading, setLoading] = useState(false);
  if (!record) return null;

  const onClick = async () => {
    if (record.status !== 'matched') return;
    setLoading(true);
    setBusy(true);
    try {
      const s = await getSettings();
      const { data: invoices } = await dp.getList<VatInvoice>('vatInvoices', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'invoiceDate', order: 'DESC' },
        filter: {},
      });
      // Ưu tiên HĐ đã chọn khi đối soát; nếu thiếu id thì tự tìm lại HĐ khớp nhất.
      let inv =
        record.matchedInvoiceId != null
          ? invoices.find((i) => String(i.id) === String(record.matchedInvoiceId))
          : undefined;
      if (!inv) {
        const cands = findMatches(record, invoices, {
          tolerancePct: s.matchAmountTolerancePct,
          toleranceAbs: s.matchAmountToleranceAbs,
          dateWindowDays: s.matchDateWindowDays,
          matchAmount: record.totalPayable || record.currentAmount || 0,
        });
        inv = cands[0]?.invoice;
      }
      if (!inv) {
        notify('Chưa xác định được HĐ VAT khớp. Hãy bấm "Đối soát" và chọn HĐ trước.', { type: 'warning' });
        return;
      }
      const { data: suppliers } = await dp.getList<Supplier>('suppliers', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      });
      const sup = findSupplierForInvoice(suppliers, inv);
      if (!sup) {
        notify('Không xác định được nhà cung cấp tương ứng với hóa đơn.', { type: 'warning' });
        return;
      }
      const order = await createPaymentOrderForMatch(dp, record, inv, sup, s);
      await dp.update('debtSlips', {
        id: record.id,
        data: { ...record, status: 'ordered', automationStatus: 'completed', automationStep: 'completed' },
        previousData: record,
      });
      notify('Đã tạo lệnh chi chờ duyệt. QR sẽ được tạo sau khi lệnh được duyệt.', { type: 'success' });
      redirect('edit', 'paymentOrders', order.id);
    } catch {
      notify('Không thể tạo lệnh chi. Vui lòng kiểm tra dữ liệu và thử lại.', { type: 'error' });
    } finally {
      setLoading(false);
      setBusy(false);
    }
  };

  return (
    <Button
      label="Tạo lệnh chi"
      variant="contained"
      color="success"
      onClick={onClick}
      disabled={busy || record.status !== 'matched'}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ReceiptLongIcon />}
    />
  );
};

// ---------- EDIT ----------
const STEP_HINT: Record<string, string> = {
  new: 'Bước 1 — Bấm “AI bóc tách” để hệ thống đọc thông tin trên phiếu.',
  extracted: 'Bước 2 — Bấm “Đối soát” rồi chọn hóa đơn VAT phù hợp.',
  matched: 'Bước 3 — Dữ liệu đã khớp, có thể tạo lệnh chi chờ duyệt.',
  ordered: 'Đã tạo lệnh chi. Chuyển sang mục “Lệnh chi” để thực hiện phê duyệt.',
};

const AUTOMATION_HINT: Record<string, string> = {
  queued: 'Đang xếp hàng xử lý tự động.',
  extracting: 'AI đang bóc tách dữ liệu chứng từ.',
  syncing_meinvoice_suppliers: 'Đang đồng bộ nhà cung cấp từ meInvoice.',
  syncing_meinvoice_invoices: 'Đang đồng bộ hóa đơn đầu vào từ meInvoice.',
  matching: 'Đang đối soát chứng từ với hóa đơn.',
  enriching_bank: 'Đang lấy thông tin ngân hàng từ chi tiết hóa đơn meInvoice.',
  creating_order: 'Đang tạo lệnh chi chờ duyệt.',
  completed: 'Quy trình tự động đã hoàn tất.',
  needs_review: 'Quy trình cần người dùng kiểm tra.',
  failed: 'Quy trình tự động đã dừng do lỗi.',
};

const RetryAutomationButton = () => {
  const record = useRecordContext<DebtSlip>();
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { busy, setBusy } = useBusy();
  if (!record || !['needs_review', 'failed'].includes(record.automationStatus || '')) return null;
  const onClick = async () => {
    setBusy(true);
    try {
      const order = await runDebtAutomation(dp, record.id);
      notify('Đã hoàn tất quy trình tự động.', { type: 'success' });
      if (order?.id != null) redirect('edit', 'paymentOrders', order.id);
      else refresh();
    } catch (error: any) {
      notify(String(error?.message || 'Quy trình vẫn cần kiểm tra thủ công.'), { type: 'warning' });
      refresh();
    } finally {
      setBusy(false);
    }
  };
  return <Button label="Thử lại tự động" onClick={onClick} disabled={busy} startIcon={<RefreshRoundedIcon />} />;
};

const WorkflowBar = () => {
  const r = useRecordContext<DebtSlip>();
  if (!r) return null;
  const activeStep = Math.max(0, STEP_ORDER.indexOf(r.status));
  return (
    <Card sx={{ width: '100%', mb: 2, overflow: 'hidden', background: 'linear-gradient(180deg, rgba(14,116,144,.035), rgba(255,255,255,1))' }}>
      <CardContent sx={{ p: { xs: 1.75, md: 2.4 }, '&:last-child': { pb: { xs: 1.75, md: 2.4 } } }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 2,
            '& .MuiStepLabel-label': { fontWeight: 750, fontSize: { xs: 11, sm: 13 } },
            '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: 'primary.main' },
            '& .MuiStepConnector-line': { borderColor: 'rgba(14,116,144,.20)' },
          }}
        >
          {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
        <Divider sx={{ mb: 1.7 }} />
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 1.5, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box>
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 850, textTransform: 'uppercase', letterSpacing: '.05em' }}>Quy trình xử lý</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {AUTOMATION_HINT[r.automationStatus || ''] || STEP_HINT[r.status]}
            </Typography>
            {r.automationError && <Alert severity="warning" sx={{ mt: 1 }}>{r.automationError}</Alert>}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, '& .MuiButton-root': { flex: { xs: '1 1 auto', sm: '0 0 auto' } } }}>
            <ExtractButton />
            <MatchButton />
            <CreateOrderButton />
            <RetryAutomationButton />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const DetailSummary = () => {
  const r = useRecordContext<DebtSlip>();
  if (!r) return null;
  const matched = r.status === 'matched' || r.status === 'ordered';
  return (
    <Card sx={{ mb: 2, overflow: 'hidden' }}>
      <CardContent sx={{ py: 1.7, '&:last-child': { pb: 1.7 }, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Tổng phải trả {matched ? '(khớp)' : ''}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 850, mt: 0.15 }}>{formatVnd(r.totalPayable)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" color={matched ? 'success' : 'default'} label={matched ? 'Dữ liệu đã khớp' : STATUS[r.status]?.label || 'Đang xử lý'} />
          {r.slipNumber && <Chip size="small" variant="outlined" label={`Phiếu ${r.slipNumber}`} />}
          {r.slipDate && <Chip size="small" variant="outlined" label={r.slipDate} />}
        </Box>
      </CardContent>
    </Card>
  );
};

const SlipImage = () => {
  const r = useRecordContext<DebtSlip>();
  if (!r?.imageDataUrl) return null;
  const openImage = () => window.open(r.imageDataUrl, '_blank', 'noopener');
  const downloadImage = () => {
    const a = document.createElement('a');
    a.href = r.imageDataUrl!;
    a.download = `don-no-${String(r.id)}.jpg`;
    a.click();
  };
  return (
    <Card sx={{ overflow: 'hidden', height: '100%' }}>
      <Box sx={{ px: 2, py: 1.4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImageRoundedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Ảnh hóa đơn gốc</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Mở ảnh kích thước đầy đủ"><IconButton size="small" onClick={openImage}><ZoomInRoundedIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Tải ảnh"><IconButton size="small" onClick={downloadImage}><DownloadRoundedIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
      </Box>
      <Box sx={{ p: { xs: 1.2, md: 1.5 }, bgcolor: '#F1F5F6', minHeight: { xs: 320, md: 620 }, display: 'grid', placeItems: 'center' }}>
        <Box
          component="img"
          src={r.imageDataUrl}
          alt="Ảnh đơn nợ"
          sx={{ width: '100%', maxHeight: { xs: 560, md: 720 }, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: '12px', display: 'block', bgcolor: '#fff', boxShadow: '0 22px 50px -38px rgba(35,20,85,.85)' }}
        />
      </Box>
    </Card>
  );
};

const ConfidenceHint = () => {
  const r = useRecordContext<DebtSlip>();
  if (r?.aiConfidence == null) return null;
  const pct = Math.round((r.aiConfidence || 0) * 100);
  return (
    <Alert severity={pct >= 70 ? 'success' : 'warning'} icon={<AutoAwesomeRoundedIcon />} sx={{ mb: 2 }}>
      <b>Độ tự tin AI: {pct}%</b> ({r.aiModel}). Hãy kiểm tra và chỉnh sửa thủ công khi cần.
    </Alert>
  );
};

const inferBusyStage = (record: DebtSlip): ActiveAutomationStage => {
  if (record.status === 'matched') return 'creating_order';
  if (record.status === 'new') return 'extracting';
  return 'queued';
};

const AutomationLoadingExperience = () => {
  const record = useRecordContext<DebtSlip>();
  const dp = useDataProvider();
  const { busy } = useBusy();
  const [liveRecord, setLiveRecord] = useState<DebtSlip | undefined>(record);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    setLiveRecord(record);
  }, [record]);

  const reportedStage = liveRecord?.automationStatus;
  const stage = reportedStage && ACTIVE_AUTOMATION_STAGES.includes(reportedStage as ActiveAutomationStage)
    ? reportedStage as ActiveAutomationStage
    : busy && liveRecord
      ? inferBusyStage(liveRecord)
      : undefined;
  const open = Boolean(stage);

  useEffect(() => {
    if (!record?.id || !open) return;
    let disposed = false;
    const poll = async () => {
      try {
        const { data } = await dp.getOne<DebtSlip>('debtSlips', { id: record.id });
        if (!disposed) setLiveRecord(data);
      } catch {
        // The active automation owns error reporting; a missed UI poll can safely retry.
      }
    };
    void poll();
    const timer = window.setInterval(poll, 700);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [dp, open, record?.id]);

  useEffect(() => {
    if (!open) {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setElapsedSeconds(Math.max(1, Math.floor((Date.now() - startedAt) / 1000)));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [open, record?.id]);

  if (!record || !stage) return null;
  const currentIndex = ACTIVE_AUTOMATION_STAGES.indexOf(stage);
  const meta = AUTOMATION_STAGE_META[stage];

  return (
    <Backdrop
      open
      sx={{
        position: 'fixed',
        zIndex: (t) => t.zIndex.modal + 2,
        bgcolor: 'rgba(9, 8, 25, .74)',
        backdropFilter: 'blur(10px) saturate(120%)',
        p: { xs: 1.5, sm: 3 },
      }}
    >
      <Box
        role="status"
        aria-live="polite"
        aria-busy="true"
        sx={{
          position: 'relative',
          isolation: 'isolate',
          width: 'min(760px, 100%)',
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
          p: { xs: 2, sm: 3.25 },
          borderRadius: { xs: '20px', sm: '28px' },
          color: '#F8F7FF',
          background: 'radial-gradient(circle at 8% 8%, rgba(115,210,255,.18), transparent 32%), radial-gradient(circle at 92% 0%, rgba(85,230,193,.14), transparent 28%), linear-gradient(145deg, #14112E 0%, #211946 54%, #15122F 100%)',
          border: '1px solid rgba(255,255,255,.13)',
          boxShadow: '0 36px 100px rgba(4,2,18,.62), inset 0 1px 0 rgba(255,255,255,.08)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            opacity: 0.28,
            backgroundImage: 'linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px)',
            backgroundSize: '34px 34px',
            maskImage: 'linear-gradient(to bottom, black, transparent 88%)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '&, & *': { animation: 'none !important' },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: { xs: 2, sm: 2.75 } }}>
          <Chip
            size="small"
            label="AUTOMATION LIVE"
            sx={{
              color: '#DDF8FF',
              bgcolor: 'rgba(115,210,255,.12)',
              border: '1px solid rgba(115,210,255,.24)',
              fontSize: 11,
              letterSpacing: '.12em',
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: 'rgba(255,255,255,.62)' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#55E6C1', boxShadow: '0 0 0 5px rgba(85,230,193,.10)', animation: 'automationPulse 1.4s ease-in-out infinite', '@keyframes automationPulse': { '0%, 100%': { opacity: .45, transform: 'scale(.8)' }, '50%': { opacity: 1, transform: 'scale(1.1)' } } }} />
            <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 700 }}>Đang xử lý</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '116px minmax(0, 1fr)' }, alignItems: 'center', gap: { xs: 2, sm: 3 }, mb: 2.75 }}>
          <Box sx={{ position: 'relative', width: 104, height: 104, mx: { xs: 'auto', sm: 0 }, display: 'grid', placeItems: 'center' }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={104}
              thickness={2.2}
              sx={{ position: 'absolute', color: 'rgba(255,255,255,.09)' }}
            />
            <CircularProgress
              size={104}
              thickness={2.2}
              sx={{ position: 'absolute', color: meta.color, filter: `drop-shadow(0 0 9px ${meta.color})`, animationDuration: '1.15s' }}
            />
            <Box sx={{ width: 68, height: 68, display: 'grid', placeItems: 'center', borderRadius: '22px', color: meta.color, bgcolor: 'rgba(255,255,255,.065)', border: '1px solid rgba(255,255,255,.10)', '& svg': { fontSize: 32 }, animation: 'automationFloat 2.2s ease-in-out infinite', '@keyframes automationFloat': { '0%, 100%': { transform: 'translateY(2px)' }, '50%': { transform: 'translateY(-4px)' } } }}>
              {meta.icon}
            </Box>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                sx={{
                  position: 'absolute',
                  width: dot === 1 ? 6 : 5,
                  height: dot === 1 ? 6 : 5,
                  borderRadius: '50%',
                  bgcolor: dot === 1 ? '#55E6C1' : '#73D2FF',
                  boxShadow: '0 0 12px currentColor',
                  animation: `automationOrbit ${2.8 + dot * .65}s linear infinite`,
                  animationDelay: `${dot * -.8}s`,
                  '@keyframes automationOrbit': {
                    from: { transform: 'rotate(0deg) translateX(58px) rotate(0deg)' },
                    to: { transform: 'rotate(360deg) translateX(58px) rotate(-360deg)' },
                  },
                }}
              />
            ))}
          </Box>

          <Box sx={{ minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="overline" sx={{ color: meta.color, fontWeight: 850, letterSpacing: '.11em' }}>
              Bước {currentIndex + 1} / {ACTIVE_AUTOMATION_STAGES.length}
            </Typography>
            <Typography component="h2" sx={{ mt: .25, fontSize: { xs: 24, sm: 30 }, lineHeight: 1.12, fontWeight: 850, letterSpacing: '-.025em' }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(246,244,255,.68)', lineHeight: 1.65, maxWidth: 510 }}>
              {meta.detail}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: .9 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.52)', fontWeight: 700 }}>Tiến độ ước tính</Typography>
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 850 }}>{meta.progress}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={meta.progress}
            sx={{
              height: 8,
              borderRadius: 99,
              bgcolor: 'rgba(255,255,255,.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 99,
                background: 'linear-gradient(90deg, #73D2FF 0%, #A78BFA 50%, #55E6C1 100%)',
                boxShadow: '0 0 18px rgba(115,210,255,.40)',
                transition: 'transform .65s cubic-bezier(.22,1,.36,1)',
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(6, minmax(0, 1fr))' }, gap: 1 }}>
          {ACTIVE_AUTOMATION_STAGES.map((item, index) => {
            const itemMeta = AUTOMATION_STAGE_META[item];
            const isCurrent = item === stage;
            const isDone = index < currentIndex;
            return (
              <Box
                key={item}
                sx={{
                  minWidth: 0,
                  p: { xs: 1, sm: 1.1 },
                  borderRadius: '14px',
                  textAlign: 'center',
                  bgcolor: isCurrent ? 'rgba(255,255,255,.10)' : 'rgba(255,255,255,.035)',
                  border: '1px solid',
                  borderColor: isCurrent ? itemMeta.color : 'rgba(255,255,255,.07)',
                  boxShadow: isCurrent ? `0 0 24px ${itemMeta.color}22` : 'none',
                  opacity: isDone || isCurrent ? 1 : .45,
                  transition: 'opacity .3s ease, border-color .3s ease, background-color .3s ease',
                }}
              >
                <Box sx={{ width: 28, height: 28, mx: 'auto', mb: .65, display: 'grid', placeItems: 'center', borderRadius: '10px', color: isDone ? '#55E6C1' : itemMeta.color, bgcolor: 'rgba(255,255,255,.06)', '& svg': { fontSize: 17 } }}>
                  {isDone ? <TaskAltRoundedIcon /> : itemMeta.icon}
                </Box>
                <Typography sx={{ color: '#fff', fontSize: 10.5, lineHeight: 1.25, fontWeight: isCurrent ? 850 : 650, overflowWrap: 'anywhere' }}>
                  {itemMeta.shortLabel}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 2.25, pt: 1.75, borderTop: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.50)' }}>
            Dữ liệu được lưu sau mỗi bước — bạn sẽ tự động chuyển sang lệnh chi khi hoàn tất.
          </Typography>
          <Typography variant="caption" sx={{ color: '#DDF8FF', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
            {elapsedSeconds > 0 ? `${elapsedSeconds} giây` : 'Đang kết nối…'}
          </Typography>
        </Box>
      </Box>
    </Backdrop>
  );
};

const AutomationResume = () => {
  const record = useRecordContext<DebtSlip>();
  const dp = useDataProvider();
  const refresh = useRefresh();
  const redirect = useRedirect();
  useEffect(() => {
    if (!record) return;
    const resumable = ['queued', 'extracting', 'syncing_meinvoice_suppliers', 'syncing_meinvoice_invoices', 'matching', 'enriching_bank', 'creating_order'];
    if (!resumable.includes(record.automationStatus || '')) return;
    void runDebtAutomation(dp, record.id)
      .then((order) => {
        if (order?.id != null) redirect('edit', 'paymentOrders', order.id);
      })
      .catch(() => undefined)
      .finally(refresh);
  }, [dp, record?.id, record?.automationStatus, redirect, refresh]);
  return null;
};

const EditBody = () => {
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <AutomationResume />
      <AutomationLoadingExperience />
      <DetailSummary />
      <WorkflowBar />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, .9fr) minmax(0, 1.35fr)' }, gap: 2, width: '100%', alignItems: 'start' }}>
        <SlipImage />
        <Card sx={{ overflow: 'hidden' }}>
          <Box sx={{ px: 2.2, py: 1.6, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Dữ liệu AI bóc tách</Typography>
            <Typography variant="caption" color="text.secondary">Kiểm tra lại các trường dữ liệu trước khi đối soát.</Typography>
          </Box>
          <CardContent sx={{ p: { xs: 1.75, md: 2.25 }, '&:last-child': { pb: { xs: 1.75, md: 2.25 } } }}>
            <ConfidenceHint />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.7 }}>
              <TextInput source="supplierName" label="Tên NCC" fullWidth sx={{ gridColumn: { sm: '1 / -1' } }} />
              <TextInput source="taxCode" label="MST" fullWidth />
              <TextInput source="slipNumber" label="Số phiếu" fullWidth />
              <TextInput source="slipDate" label="Ngày (yyyy-mm-dd)" fullWidth />
              <NumberInput source="currentAmount" label="Tiền đơn lần này" fullWidth />
              <NumberInput source="oldDebt" label="Nợ cũ" fullWidth />
              <NumberInput source="totalPayable" label="Tổng phải trả (khớp)" fullWidth />
              <TextInput source="bankInfoRaw" label="Thông tin ngân hàng ghi trên phiếu" fullWidth sx={{ gridColumn: { sm: '1 / -1' } }} />
              <TextInput source="rawText" label="Nội dung AI đọc được" fullWidth multiline minRows={8} sx={{ gridColumn: { sm: '1 / -1' } }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export const DebtSlipEdit = () => {
  const [busy, setBusy] = useState(false);
  return (
    <BusyCtx.Provider value={{ busy, setBusy }}>
      <Box sx={pageShellSx}>
        <PageHeader
          title="Chi tiết đơn nợ"
          subtitle="Xem chứng từ gốc, kiểm tra dữ liệu AI, đối soát và chuẩn bị lệnh thanh toán."
          icon={<ReceiptLongIcon />}
        />
        <Edit title="Chi tiết đơn nợ" sx={formPageSx}>
          <SimpleForm>
            <EditBody />
          </SimpleForm>
        </Edit>
      </Box>
    </BusyCtx.Provider>
  );
};
